import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Bell, Calendar, Inbox, ChevronLeft, ChevronRight, CheckSquare } from "lucide-react";
import service from "../../services/service";
import endpoints from "../../services/endpoints";
import { setNotifications, markSingleAsRead, markAllAsReadComplete } from "../../features/notifications/notificationSlice";
import "../../profile.css";

const formatNotifType = (typeString) => {
  if (!typeString) return "Notification";
  return typeString.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatNotifDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const timeOptions = { hour: "2-digit", minute: "2-digit", hour12: true };
  const timeStr = date.toLocaleTimeString("en-US", timeOptions);
  return `${timeStr} | ${date.getDate()} ${date.toLocaleString("en-US", { month: "short" })}, ${date.getFullYear()}`;
};

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  
  // FIXED: Subscribed to same Redux notifications array state 
  const notifications = useSelector((state) => state.notifications.items || []);
  const [loading, setLoading] = useState(notifications.length === 0);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchAllNotifications = async () => {
      try {
        if (!accessToken) return;
        const res = await service.get(endpoints.notifications.viewAll, {}, {
          headers: { Authorization: `Bearer ${accessToken}` },
          skipToast: true,
        });
        dispatch(setNotifications(res?.data?.data || []));
      } catch (error) {
        console.error("ERROR FETCHING ALL NOTIFICATIONS:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllNotifications();
  }, [accessToken, dispatch]);

  // Fires global Redux state modifications instantly updating the Header
  const handleNotificationClick = async (notifId) => {
  try {
    if (!accessToken || !notifId) return;

    // Update Redux immediately
    dispatch(markSingleAsRead(notifId));

    // Update backend
    await service.patch(
      endpoints.notifications.markSingleRead(notifId),
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        skipToast: true,
      }
    );
  } catch (error) {
    console.error("ERROR MARKING SINGLE NOTIFICATION:", error);
  }
};

  // Fires global complete clear out updating Header dot instantly
  const handleMarkAllRead = async () => {
    try {
      if (!accessToken) return;
      dispatch(markAllAsReadComplete());

      await service.patch(endpoints.notifications.markAllRead, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
        skipToast: true,
      });
    } catch (error) {
      console.error("ERROR MARKING ALL AS READ:", error);
    }
  };

  // PAGINATION
  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = notifications.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasUnread = notifications.some((n) => n.isRead === false);

  return (
    <div className="notifications-page-container">
      <div className="notifications-page-header">
        <div className="header-title-area">
          <Bell className="header-icon" size={24} />
          <div className="title-sub-wrapper">
            <h1>Notifications & Alerts</h1>
            <span className="notif-count-text">{notifications.length} Total items listed</span>
          </div>
        </div>

        {!loading && notifications.length > 0 && (
          <button className="mark-all-read-btn" onClick={handleMarkAllRead} disabled={!hasUnread}>
            <CheckSquare size={16} />
            <span>Mark All As Read</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="notif-page-loader"><div className="spinner"></div><p>Loading your alerts...</p></div>
      ) : notifications.length === 0 ? (
        <div className="notif-page-empty">
          <Inbox size={48} className="empty-icon" />
          <h3>All caught up!</h3>
          <p>You don't have any incoming system notifications at the moment.</p>
        </div>
      ) : (
        <>
          <div className="notif-page-list">
            {currentNotifications.map((notif) => {
              const isUnread = notif.isRead === false;
              return (
                <div key={notif.id || notif._id} className={`notif-page-item ${isUnread ? "unread-alert" : "read-alert"}`} onClick={() => handleNotificationClick(notif.id || notif._id)}>
                  <div className="notif-page-body">
                    <div className="notif-page-meta">
                      <h3 className="notif-page-title">{formatNotifType(notif.type)}</h3>
                      {isUnread && <span className="new-tag">New</span>}
                    </div>
                    <p className="notif-page-desc">{notif.message}</p>
                    <div className="notif-page-time-row">
                      <Calendar size={12} />
                      <span>{formatNotifDate(notif.createdAt || notif.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="notif-pagination-container">
              <button className="pagination-arrow-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                <ChevronLeft size={18} />
              </button>
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button key={pageNum} className={`pagination-num-btn ${currentPage === pageNum ? "active-page" : ""}`} onClick={() => handlePageChange(pageNum)}>
                    {pageNum}
                  </button>
                ))}
              </div>
              <button className="pagination-arrow-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}